import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControlLabel,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Switch,
  Typography,
} from '@mui/material';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { isAdminEmail, loadAdminCourses, updateCourseEnabled, loadAdminScenarios, updateScenarioEnabled, loadAdminSessions, loadDemoSetting, updateDemoSetting, upsertAdminCourse, upsertAdminScenario, loadAdminTests, upsertAdminTest, updateTestEnabled } from '../services/admin';

const sampleCourse = JSON.stringify({
  id: 'example-course',
  title: 'Example Course',
  description: 'Short description for the course.',
  pages: [
    {
      id: 'first-page',
      title: 'First Page',
      content: ['Write lesson content here.'],
      tasks: [],
    },
  ],
}, null, 2);

const sampleScenario = JSON.stringify({
  id: 'example-scenario',
  title: 'Example Scenario',
  description: 'Short description for the presentation scenario.',
  steps: [
    {
      title: 'First step',
      description: 'Describe what the presenter should demonstrate.',
      command: 'supervisorctl -c /etc/supervisor/supervisord.conf status example-web',
      tasks: [
        { title: 'Restart the service', command: 'supervisorctl -c /etc/supervisor/supervisord.conf restart example-web' },
      ],
    },
  ],
}, null, 2);

const sampleTest = JSON.stringify({ id: 'example-test', title: 'Example test', description: 'Command-line quiz', questions: [{ prompt: 'Which command identifies the current user?', choices: ['whoami', 'pwd'], correctIndex: 0 }] }, null, 2);

function formatDate(value) {
  if (!value) {
    return '—';
  }
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'medium',
  }).format(new Date(value));
}

export default function AdminPage() {
  const { currentUser } = useAuth();
  const [tab, setTab] = useState(0);
  const [sessions, setSessions] = useState([]);
  const [courses, setCourses] = useState([]);
  const [scenarios, setScenarios] = useState([]);
  const [tests, setTests] = useState([]);
  const [demoEnabled, setDemoEnabled] = useState(false);
  const [courseJson, setCourseJson] = useState(sampleCourse);
  const [scenarioJson, setScenarioJson] = useState(sampleScenario);
  const [testJson, setTestJson] = useState(sampleTest);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const isAdmin = useMemo(() => isAdminEmail(currentUser?.email), [currentUser?.email]);

  const refresh = async () => {
    setLoading(true);
    setError('');
    try {
      const [sessionData, courseData, scenarioData, testData, demoData] = await Promise.all([
        loadAdminSessions(),
        loadAdminCourses(),
        loadAdminScenarios(),
        loadAdminTests(),
        loadDemoSetting(),
      ]);
      setSessions(sessionData.sessions || []);
      setCourses(courseData.courses || []);
      setScenarios(scenarioData.scenarios || []);
      setTests(testData.tests || []);
      setDemoEnabled(Boolean(demoData.enabled));
    } catch (err) {
      setError(err.message || 'Failed to load admin data.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) {
      refresh();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  const submitCourse = async () => {
    setLoading(true);
    setError('');
    setMessage('');
    try {
      JSON.parse(courseJson);
      const result = await upsertAdminCourse(courseJson);
      setMessage(`Saved ${result.upserted || 0} course record(s).`);
      await refresh();
      setTab(1);
    } catch (err) {
      setError(err.message || 'Failed to save course JSON.');
    } finally {
      setLoading(false);
    }
  };

  const submitScenario = async () => {
    setLoading(true);
    setError('');
    setMessage('');
    try {
      JSON.parse(scenarioJson);
      const result = await upsertAdminScenario(scenarioJson);
      setMessage(`Saved ${result.upserted || 0} scenario record(s).`);
      await refresh();
      setTab(2);
    } catch (err) {
      setError(err.message || 'Failed to save scenario JSON.');
    } finally {
      setLoading(false);
    }
  };

  const changeDemoSetting = async (event) => {
    const enabled = event.target.checked;
    setLoading(true);
    setError('');
    setMessage('');
    try {
      const result = await updateDemoSetting(enabled);
      setDemoEnabled(Boolean(result.enabled));
      setMessage(`Demo is now ${result.enabled ? 'enabled' : 'disabled'}.`);
    } catch (err) {
      setError(err.message || 'Failed to update demo availability.');
    } finally {
      setLoading(false);
    }
  };

  const changeCourseSetting = async (course, enabled) => {
    setLoading(true);
    setError('');
    setMessage('');
    try {
      const result = await updateCourseEnabled(course.courseId, enabled);
      setCourses((current) => current.map((item) => (
        item.courseId === course.courseId ? { ...item, enabled: result.enabled } : item
      )));
      setMessage(`${course.title} is now ${result.enabled ? 'enabled' : 'disabled'}.`);
    } catch (err) {
      setError(err.message || 'Failed to update course availability.');
    } finally {
      setLoading(false);
    }
  };

  const changeScenarioSetting = async (scenario, enabled) => {
    setLoading(true);
    setError('');
    setMessage('');
    try {
      const result = await updateScenarioEnabled(scenario.scenarioId, enabled);
      setScenarios((current) => current.map((item) => (
        item.scenarioId === scenario.scenarioId ? { ...item, enabled: result.enabled } : item
      )));
      setMessage(`${scenario.title} is now ${result.enabled ? 'enabled' : 'disabled'}.`);
    } catch (err) {
      setError(err.message || 'Failed to update scenario availability.');
    } finally {
      setLoading(false);
    }
  };

  const submitTest = async () => {
    setLoading(true); setError(''); setMessage('');
    try { JSON.parse(testJson); const result = await upsertAdminTest(testJson); setMessage(`Saved ${result.upserted || 0} test record(s).`); await refresh(); setTab(3); }
    catch (err) { setError(err.message || 'Failed to save test JSON.'); } finally { setLoading(false); }
  };

  const changeTestSetting = async (test, enabled) => {
    setLoading(true); setError(''); setMessage('');
    try { const result = await updateTestEnabled(test.testId, enabled); setTests((items) => items.map((item) => item.testId === test.testId ? { ...item, enabled: result.enabled } : item)); setMessage(`${test.title} is now ${result.enabled ? 'enabled' : 'disabled'}.`); }
    catch (err) { setError(err.message || 'Failed to update test availability.'); } finally { setLoading(false); }
  };

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="h4" component="h1" gutterBottom>
          Admin
        </Typography>
        <Typography color="text.secondary">
          View sessions, courses, and the JSON-backed presentation scenarios loaded from the database.
        </Typography>
      </Box>

      {error && <Alert severity="error">{error}</Alert>}
      {message && <Alert severity="success">{message}</Alert>}

      <Stack direction="row" spacing={1}>
        <Button variant="outlined" onClick={refresh} disabled={loading}>
          Refresh
        </Button>
      </Stack>

      <Paper>
        <Tabs value={tab} onChange={(_, value) => setTab(value)}>
          <Tab label="Sessions" />
          <Tab label="Courses" />
          <Tab label="Scenarios" />
          <Tab label="Tests" />
          <Tab label="Add course JSON" />
          <Tab label="Add scenario JSON" />
          <Tab label="Add test JSON" />
          <Tab label="Settings" />
        </Tabs>
      </Paper>

      {tab === 0 && (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Session</TableCell>
                <TableCell>Course</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Destroyed</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sessions.map((session) => (
                <TableRow key={session.id}>
                  <TableCell>{session.sessionId}</TableCell>
                  <TableCell>{session.courseId}</TableCell>
                  <TableCell>
                    <Chip
                      label={session.active ? 'Active' : 'Closed'}
                      color={session.active ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{formatDate(session.createdAt)}</TableCell>
                  <TableCell>{formatDate(session.destroyedAt)}</TableCell>
                </TableRow>
              ))}
              {!sessions.length && (
                <TableRow>
                  <TableCell colSpan={5}>No session records found.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {tab === 1 && (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Course ID</TableCell>
                <TableCell>Title</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Customer availability</TableCell>
                <TableCell>Updated</TableCell>
                <TableCell>Payload</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {courses.map((course) => (
                <TableRow key={course.courseId}>
                  <TableCell>{course.courseId}</TableCell>
                  <TableCell>{course.title}</TableCell>
                  <TableCell>{course.description || '—'}</TableCell>
                  <TableCell>
                    <FormControlLabel
                      control={(
                        <Switch
                          checked={course.enabled}
                          onChange={(event) => changeCourseSetting(course, event.target.checked)}
                          disabled={loading}
                        />
                      )}
                      label={course.enabled ? 'Enabled' : 'Disabled'}
                    />
                  </TableCell>
                  <TableCell>{formatDate(course.updatedAt)}</TableCell>
                  <TableCell>
                    <Box
                      component="pre"
                      sx={{
                        m: 0,
                        maxWidth: 420,
                        maxHeight: 160,
                        overflow: 'auto',
                        fontSize: '0.75rem',
                        whiteSpace: 'pre-wrap',
                      }}
                    >
                      {JSON.stringify(course.coursePayload, null, 2)}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
              {!courses.length && (
                <TableRow>
                  <TableCell colSpan={6}>No courses found.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {tab === 2 && (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead><TableRow><TableCell>Scenario ID</TableCell><TableCell>Title</TableCell><TableCell>Description</TableCell><TableCell>Steps</TableCell><TableCell>Availability</TableCell><TableCell>Updated</TableCell></TableRow></TableHead>
            <TableBody>
              {scenarios.map((scenario) => (
                <TableRow key={scenario.scenarioId}>
                  <TableCell>{scenario.scenarioId}</TableCell><TableCell>{scenario.title}</TableCell><TableCell>{scenario.description || '—'}</TableCell><TableCell>{scenario.scenarioPayload?.steps?.length || 0}</TableCell>
                  <TableCell><FormControlLabel control={<Switch checked={scenario.enabled} onChange={(event) => changeScenarioSetting(scenario, event.target.checked)} disabled={loading} />} label={scenario.enabled ? 'Enabled' : 'Disabled'} /></TableCell>
                  <TableCell>{formatDate(scenario.updatedAt)}</TableCell>
                </TableRow>
              ))}
              {!scenarios.length && <TableRow><TableCell colSpan={6}>No scenarios found.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {tab === 3 && (
        <TableContainer component={Paper}><Table size="small"><TableHead><TableRow><TableCell>Test ID</TableCell><TableCell>Title</TableCell><TableCell>Questions</TableCell><TableCell>Availability</TableCell><TableCell>Updated</TableCell></TableRow></TableHead><TableBody>{tests.map((test) => <TableRow key={test.testId}><TableCell>{test.testId}</TableCell><TableCell>{test.title}</TableCell><TableCell>{test.testPayload?.questions?.length || 0}</TableCell><TableCell><FormControlLabel control={<Switch checked={test.enabled} onChange={(event) => changeTestSetting(test, event.target.checked)} disabled={loading} />} label={test.enabled ? 'Enabled' : 'Disabled'} /></TableCell><TableCell>{formatDate(test.updatedAt)}</TableCell></TableRow>)}{!tests.length && <TableRow><TableCell colSpan={5}>No tests found.</TableCell></TableRow>}</TableBody></Table></TableContainer>
      )}

      {tab === 4 && (
        <Paper sx={{ p: 2 }}>
          <Stack spacing={2}>
            <Typography variant="h6">Add or update a course</Typography>
            <Typography color="text.secondary">
              Paste a single course object, an array of course objects, or an object with a top-level
              <code> courses </code> array. Each course must include an <code>id</code> or <code>courseId</code> and a <code>title</code>. Use the availability switch in the Courses tab to control whether customers can access it.
            </Typography>
            <TextField
              label="Course JSON"
              value={courseJson}
              onChange={(event) => setCourseJson(event.target.value)}
              multiline
              minRows={18}
              fullWidth
              InputProps={{ sx: { fontFamily: 'monospace' } }}
            />
            <Button variant="contained" onClick={submitCourse} disabled={loading}>
              Save course JSON
            </Button>
          </Stack>
        </Paper>
      )}

      {tab === 5 && (
        <Paper sx={{ p: 2 }}>
          <Stack spacing={2}>
            <Typography variant="h6">Add or update a scenario</Typography>
            <Typography color="text.secondary">
              Paste a single scenario object, an array of scenario objects, or an object with a top-level
              <code> scenarios </code> array. Each scenario must include an <code>id</code> or <code>scenarioId</code>, a <code>title</code>, and at least one step.
            </Typography>
            <TextField
              label="Scenario JSON"
              value={scenarioJson}
              onChange={(event) => setScenarioJson(event.target.value)}
              multiline
              minRows={18}
              fullWidth
              InputProps={{ sx: { fontFamily: 'monospace' } }}
            />
            <Button variant="contained" onClick={submitScenario} disabled={loading}>
              Save scenario JSON
            </Button>
          </Stack>
        </Paper>
      )}

      {tab === 6 && (
        <Paper sx={{ p: 2 }}><Stack spacing={2}><Typography variant="h6">Add or update a test</Typography><Typography color="text.secondary">Paste one test, an array, or an object with a <code>tests</code> array. Every question needs a prompt, at least two command choices, and a zero-based correctIndex.</Typography><TextField label="Test JSON" value={testJson} onChange={(event) => setTestJson(event.target.value)} multiline minRows={18} fullWidth InputProps={{ sx: { fontFamily: 'monospace' } }} /><Button variant="contained" onClick={submitTest} disabled={loading}>Save test JSON</Button></Stack></Paper>
      )}

      {tab === 7 && (
        <Paper sx={{ p: 2 }}>
          <Stack spacing={1}>
            <Typography variant="h6">Public demo</Typography>
            <Typography color="text.secondary">
              When disabled, visitors cannot open the <code>/demo</code> endpoint.
            </Typography>
            <FormControlLabel
              control={<Switch checked={demoEnabled} onChange={changeDemoSetting} disabled={loading} />}
              label={demoEnabled ? 'Demo enabled' : 'Demo disabled'}
            />
          </Stack>
        </Paper>
      )}
    </Stack>
  );
}
